/**
 * Shared money formatter / parser for the whole theme.
 *
 * Shopify gives every presentment currency its own money format (symbol before
 * or after the amount, space / comma / period / apostrophe separators, with or
 * without decimals). Liquid's `| money` already honours it; this file does the
 * same for JavaScript so no script has to hardcode a symbol or a separator.
 *
 * The ACTIVE format comes from, in order:
 *   1. window.theme.moneyFormat   (layout inline script: shop.money_format)
 *   2. #wetheme-global JSON       ("moneyFormat": shop.money_format)
 *   3. window.Shopify.money_format
 *   4. "{{amount}}"               (last resort: number only, never a wrong symbol)
 *
 * API (all amounts are integer CENTS in the active presentment currency):
 *   themeMoney.format(cents [, format [, opts]])  -> "1 234,56 kr"
 *                                             opts.trimZeros: whole amounts drop the
 *                                             decimals ("30 kr" instead of "30,00 kr")
 *   themeMoney.formatValue(cents, name [, trimZeros]) -> "1 234,56" (number part only)
 *   themeMoney.parse(text [, format])      -> cents (integer) or NaN
 *   themeMoney.toCents(value [, format])   -> cents from a number, a cents
 *                                             string ("2995") or a display string
 *   themeMoney.getFormat()                 -> active format string
 *   themeMoney.normalizeFormat(fmt)        -> "€{{x}}" becomes "{{x}}€" (euro suffix rule);
 *                                             anything else is returned unchanged
 * Euro rule: prices print as 29,95€ (symbol after, no space). getFormat() and format()
 * apply it, so a "€{{amount_with_comma_separator}}" format from any source is covered.
 * Also exposed as Shopify.formatMoney(cents, format) when not already defined.
 */
(function (window, document) {
  'use strict';

  if (window.themeMoney && window.themeMoney.__v >= 2) return;

  var PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;
  // Euro prices read AMOUNT€ (29,95€), never €AMOUNT. Only a bare "€{{placeholder}}" is
  // rewritten; every other currency format is used exactly as Shopify defines it.
  // Liquid twin: snippets/money.liquid + snippets/money-format-string.liquid.
  var EURO_PREFIX = /^\s*€\s*(\{\{\s*\w+\s*\}\})\s*$/;

  // name -> [decimals, thousands separator, decimal separator]
  var STYLES = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_period_and_space_separator: [2, ' ', '.'],
    amount_no_decimals_with_period_and_space_separator: [0, ' ', '.']
  };

  function normalizeFormat(fmt) {
    var m = typeof fmt === 'string' ? fmt.match(EURO_PREFIX) : null;
    return m ? m[1] + '€' : fmt;
  }

  function getFormat() {
    var fmt;
    try {
      if (window.theme && typeof window.theme.moneyFormat === 'string') fmt = window.theme.moneyFormat;
    } catch (e) {}
    if (!fmt) {
      try {
        var el = document.getElementById('wetheme-global');
        if (el) fmt = JSON.parse(el.textContent).moneyFormat;
      } catch (e) {}
    }
    if (!fmt && window.Shopify && typeof window.Shopify.money_format === 'string') fmt = window.Shopify.money_format;
    if (typeof fmt !== 'string' || !PLACEHOLDER.test(fmt)) fmt = '{{amount}}';
    return normalizeFormat(fmt);
  }

  function placeholderName(format) {
    var m = String(format || getFormat()).match(PLACEHOLDER);
    return m ? m[1] : null;
  }

  // Integer maths only: no float surprises, half-up rounding like Shopify.
  function formatValue(cents, name, trimZeros) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = Number(cents);
    if (!isFinite(cents)) cents = 0;
    cents = Math.round(cents);

    var style = STYLES[name] || STYLES.amount;
    var decimals = trimZeros && cents % 100 === 0 ? 0 : style[0];
    var negative = cents < 0;
    var abs = Math.abs(cents);
    var whole;
    var frac = '';

    if (decimals === 0) {
      whole = Math.round(abs / 100);
    } else {
      whole = Math.floor(abs / 100);
      frac = String(abs % 100);
      if (frac.length < 2) frac = '0' + frac;
    }

    var wholeStr = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, style[1]);
    return (negative && abs !== 0 ? '-' : '') + wholeStr + (decimals ? style[2] + frac : '');
  }

  function format(cents, fmt, opts) {
    var f = typeof fmt === 'string' && PLACEHOLDER.test(fmt) ? normalizeFormat(fmt) : getFormat();
    var trim = !!(opts && opts.trimZeros);
    return f.replace(PLACEHOLDER, function () {
      return formatValue(cents, placeholderName(f), trim);
    });
  }

  // Display string -> cents. Currency symbols / codes, before or after, are ignored.
  function parse(text, fmt) {
    if (typeof text === 'number') return Math.round(text);
    if (typeof text !== 'string') return NaN;
    var chunk = text.match(/\d[\d\s.,'\u2019\u00a0\u202f]*\d|\d/);
    if (!chunk) return NaN;
    var num = chunk[0];
    var negative = /-\s*$/.test(text.slice(0, chunk.index)) || /^\s*-/.test(text) || /\u2212/.test(text.slice(0, chunk.index));
    var name = placeholderName(typeof fmt === 'string' ? fmt : getFormat());
    var style = name ? STYLES[name] : null;
    var decSep = style ? style[2] : null;
    var decimals = style ? style[0] : 2;
    var cents;

    if (!style) {
      // Unknown placeholder: guess the decimal separator (1-2 trailing digits).
      var m = num.match(/[.,](\d{1,2})$/);
      decimals = m ? 2 : 0;
      decSep = m ? num.charAt(num.length - m[1].length - 1) : null;
    }

    if (decimals === 0) {
      cents = parseInt(num.replace(/[^\d]/g, ''), 10) * 100;
    } else {
      var idx = num.lastIndexOf(decSep);
      var intPart = idx === -1 ? num : num.slice(0, idx);
      var fracPart = idx === -1 ? '' : num.slice(idx + 1);
      intPart = intPart.replace(/[^\d]/g, '');
      fracPart = fracPart.replace(/[^\d]/g, '');
      fracPart = (fracPart + '00').slice(0, 2);
      cents = parseInt(intPart || '0', 10) * 100 + parseInt(fracPart, 10);
    }
    return negative ? -cents : cents;
  }

  // number -> as is; "2995" / "-500" -> cents; anything else is a display string.
  function toCents(value, fmt) {
    if (typeof value === 'number') return Math.round(value);
    if (typeof value === 'string' && /^\s*-?\d+\s*$/.test(value)) return parseInt(value, 10);
    return parse(value, fmt);
  }

  var api = {
    __v: 2,
    format: format,
    normalizeFormat: normalizeFormat,
    formatValue: formatValue,
    parse: parse,
    toCents: toCents,
    getFormat: getFormat,
    styles: STYLES
  };

  window.themeMoney = api;
  window.Shopify = window.Shopify || {};
  if (typeof window.Shopify.formatMoney !== 'function') window.Shopify.formatMoney = format;
})(window, document);
